import React from 'react'
import { useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as R from 'ramda'

import { useControlledForm, useFormReset } from 'hooks'
import { mapPropsToInternalState } from './actions'
import { DEADLINE_FIELD, MESSAGES_FIELD } from './model'
import { getSchema } from './schema'

export const useSendTestsMessageForm = (state, handlers) => {
  const { candidates, company, name, deadline, templates, isOpen } = state
  const { onConfirm } = handlers

  const [term, setTerm] = React.useState(null)
  const [shouldReset, setShouldReset] = React.useState(false)

  const schema = getSchema()

  const internalState = React.useMemo(
    () =>
      mapPropsToInternalState({
        candidates,
        company,
        deadline: term || deadline,
        templates,
        name,
      }),
    [candidates, company, deadline, templates, term],
  )

  const handleFormData = React.useCallback(
    async data => {
      onConfirm(
        { candidates: data[MESSAGES_FIELD] },
        { onSuccess: () => setShouldReset(true) },
      )
    },
    [onConfirm],
  )

  const {
    control,
    formState,
    handleFormReset,
    handleFormSubmit,
    setValue,
    isDirtyOrInvalid,
    watch,
  } = useControlledForm({
    values: internalState,
    resolver: yupResolver(schema),
    handleFormData,
  })

  const { fields } = useFieldArray({ control, name: MESSAGES_FIELD })

  useFormReset(
    { shouldReset: R.and(shouldReset, formState.isSubmitSuccessful) },
    handleFormReset,
  )

  React.useEffect(() => {
    if (isOpen) {
      setShouldReset(false)
    }
  }, [isOpen])

  React.useEffect(() => {
    const subscription = watch((values, { name }) => {
      const index = name ? /[\d*]/g.exec(name)?.[0] : null

      if (index) {
        setTerm(values.messages[Number(index)]?.deadline)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [watch, isDirtyOrInvalid])

  React.useEffect(() => {
    if (term) {
      R.addIndex(R.forEach)((_, index) => {
        setValue(`${MESSAGES_FIELD}[${index}].${DEADLINE_FIELD}`, term)
      }, fields)
    }
  }, [term])

  return {
    state: { control, fields, formState, isDirtyOrInvalid },
    handlers: { handleFormSubmit, handleFormReset },
  }
}
